import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmComponent } from 'projects/ui-library/src/lib/components/confirm/confirm.component';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { ImportDashboardComponent } from 'src/app/components/project-pages/dashboard/dialogs/import-dashboard/import-dashboard.component';
import { PutDashboard } from 'src/app/interfaces/api/dashboard';
import { Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-dashboard-manager',
  imports: [
    MatIconModule,
    TranslateModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    DragDropModule,
    MatInputModule,
    MatButtonModule,
    ButtonComponent,
    NoDataComponent
  ],
  standalone: true,
  templateUrl: './dashboard-manager.component.html',
  styleUrl: './dashboard-manager.component.scss'
})

export class DashboardManagerComponent implements OnInit, OnDestroy {
  // Services
  private dashboardStore = inject(DashboardStore);
  private projectService = inject(ProjectService);

  // Variables
  mailingList: any[] = [];
  dashboardName: string = '';
  showEditDash: boolean = false;
  selectedDashboardId: string | null = null;
  project!: Project;
  user!: User;
  refreshValue: number = 1;
  refreshUnit: string = 'hour';
  selectedMailingList: string[] = [];
  optionsList: any[] = [];
  currentProjectIndex: number = 0;
  units: any;
  currentDashboard = signal<Dashboard | null>(null);
  private initialDashboardId: string | null;
  destroy$ = new Subject<void>();

  //Signals
  dashboardList: Signal<Dashboard[]> = this.dashboardStore.sortedDashboards;
  readonly canManageProject: Signal<boolean> = inject(AuthService).canManageProject;

  constructor(
    public dialogRef: MatDialogRef<DashboardManagerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dashboardService: DashboardService,
    private unitService: UnitService,
    private authService: AuthService,
    private userService: UserService,
    public translate: TranslateService,
    private dialog: MatDialog,
    private pop: Pop,
  ) {
    this.user = this.authService.currentUser();
    this.project = this.projectService.currentProjectSignal();
    this.initialDashboardId = this.data.currentDashboardId;

    this.dashboardStore.setCurrentProjectId(this.project.id);

    this.userService.getUsersLight(this.project.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.optionsList = res || [];

          this.optionsList.unshift({
            id: 'mailingList',
            email: 'Mailing list',
          });

          this.optionsList.unshift({ id: 'all', email: 'All' });
        },
        error: (err) => {
          console.warn('⚠️ Could not load users for mailing list:', err);

          this.optionsList = [
            { id: 'all', email: 'All' },
            { id: 'mailingList', email: 'Mailing list' }
          ];
        }
      });

    this.unitService.getUnits({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.units = res || [];
        },
        error: (err) => {
          console.warn('⚠️ Could not load units:', err);
          this.units = [];
        }
      });
  }

  ngOnInit() {
    const dashboards = this.dashboardList();

    if (dashboards.length === 0) {
      console.warn('⚠️ No dashboards available');
      return;
    }

    if (this.initialDashboardId) {
      this.handleClick(this.initialDashboardId);
    } else if (dashboards.length > 0 && dashboards[0]) {
      this.handleClick(dashboards[0].id);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectionChange(event: any) { }

  addSettings = (dashboard: Dashboard) => {
    if (!dashboard.projectSettings) dashboard.projectSettings = [];

    dashboard.projectSettings.push({
      project: this.project.id,
      reportFrequency: 'never',
      mailingList: [],
      index: -1,
    });

    return dashboard;
  };

  handleClick(dashboardId: string) {
    const dashboards = this.dashboardStore.dashboards();
    const foundDashboard = dashboards.find(d => d.id === dashboardId);

    if (!foundDashboard) return;

    const dashboardCopy = JSON.parse(JSON.stringify(foundDashboard));

    if (!dashboardCopy.autoRefresh) {
      dashboardCopy.autoRefresh = { value: 1, unit: 'hour' };
    }

    if (!dashboardCopy.projectSettings) {
      this.addSettings(dashboardCopy);
      this.currentProjectIndex = 0;
    } else {
      this.currentProjectIndex = dashboardCopy.projectSettings.findIndex(
        (s: any) => s.project == this.project.id
      );

      if (this.currentProjectIndex === -1) {
        this.addSettings(dashboardCopy);
        this.currentProjectIndex = dashboardCopy.projectSettings.length - 1;
      }
    }

    this.currentDashboard.set(dashboardCopy);
  }

  addDashboard() {
    const dashboards = this.dashboardList();

    const maxProjectIndex = dashboards.length > 0
      ? Math.max(...dashboards.map(d => {
        const projectSetting = d.projectSettings?.find((s: any) => s.project == this.project.id);
        return projectSetting?.index ?? -1;
      }))
      : -1;

    const newProjectIndex = maxProjectIndex + 1;
    const newIndex = this.dashboardList.length + 1;

    const jsonDashboard = {
      title: `Dashboard ${newIndex}`,
      index: newIndex.toString(),
      charts: [],
      accessKey: '',
      mailingList: [],
      projects: [this.project.id],
      projectSettings: [
        {
          project: String(this.project.id),
          reportFrequency: 'never' as 'never',
          mailingList: [],
          index: newProjectIndex,
        },
      ],
      autoRefresh: {
        value: 1,
        unit: "hour" as "hour",
      },
      filters: {},
    };

    this.dashboardService.addDashboard(jsonDashboard)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.handleClick(res.id);
          this.pop.success(this.translate.instant('dashboard.manager.add_dashboard_success'));
        },
        error: (err) => {
          console.error('❌ Error adding dashboard:', err);
          this.pop.error(this.translate.instant('status.error'));
        }
      });
  }

  duplicateDashboard(dashboard: { id: string }) {
    this.dashboardService.duplicateDashboard(dashboard.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.handleClick(res.id);
          this.pop.success(this.translate.instant('dashboard.manager.duplicate_dashboard'));
        },
        error: (err) => {
          console.error('❌ Error duplicating dashboard:', err);
          this.pop.error(this.translate.instant('status.error'));
        }
      });
  }

  import() {
    const dialogRef = this.dialog.open(ImportDashboardComponent, {
      width: '650px',
      data: {
        title: this.translate.instant('workflows.import_dashboard'),
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.dashboards && result.dashboards.length > 0) {
        result.dashboards.forEach((dashboard: any) => {
          const importAction$ = result.type == 'duplicate' ? this.dashboardService.duplicateDashboard(dashboard.id) : this.dashboardService.instantiateDashboard(dashboard.id);

          importAction$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              this.handleClick(res.id);
            },
            error: (err) => {
              console.error('❌ Error importing dashboard:', err);
              this.pop.error(this.translate.instant('status.error'));
            }
          });
        });
      }
    });
  }

  unlink() {
    const current = this.currentDashboard();
    if (!current) return;

    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '300px',
      data: {
        title: 'Délier dashboard',
        content: `Voulez-vous vraiment délier le dashboard "${current.title}" ?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result.dismissed) {
        const id = current.id;

        this.dashboardService.duplicateDashboard(id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (newDashboard) => {
              this.dashboardService.removeDashboard(id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => {
                    this.handleClick(newDashboard.id);
                    this.pop.success(this.translate.instant('dashboard.manager.unlink_success'));
                  },
                  error: (err) => {
                    console.error('❌ Error removing old dashboard:', err);
                    this.pop.error(this.translate.instant('status.error'));
                  }
                });
            },
            error: (err) => {
              console.error('❌ Error duplicating dashboard:', err);
              this.pop.error(this.translate.instant('status.error'));
            }
          });
      }
    });
  }

  deleteDashboard(dashboard: any) {
    if (!this.canManageProject()) return;

    const isSharedDashboard = dashboard.projects && Array.isArray(dashboard.projects) && dashboard.projects.length > 1;

    if (isSharedDashboard) {
      const dialogRef = this.dialog.open(ConfirmComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('dashboard.manager.delete_all'),
          icon: 'delete',
          content: this.translate.instant('dashboard.manager.delete_all_desc'),
          cancelText: this.translate.instant('dashboard.manager.delete_all_cancel'),
          cancelColor: 'warning',
          cancelIcon: 'delete',
          okText: this.translate.instant('dashboard.manager.delete_all_ok'),
          okColor: 'danger',
          okIcon: 'delete',
        },
      });

      dialogRef.afterClosed().subscribe((res) => {
        if (res?.ok) {
          this.requestDeleteDashboard(dashboard, true);
        } else if (res?.cancel) {
          this.requestDeleteDashboard(dashboard, false);
        }
      });
    } else {
      this.requestDeleteDashboard(dashboard, false);
    }
  }

  requestDeleteDashboard(dashboard: any, allProjects = false) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: {
        title: this.translate.instant('dashboard.manager.delete'),
        content: this.translate.instant('delete_confirm') + `\r\n "${dashboard.title}"`,
        icon: 'delete',
        okText: this.translate.instant('form.delete'),
        okColor: 'danger',
        okIcon: 'delete',
        cancelText: this.translate.instant('form.close'),
        cancelColor: 'outline',
        cancelIcon: 'close'
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result.dismissed) {
        const current = this.currentDashboard();

        if (current && current.id === dashboard.id) {
          const dashboards = this.dashboardList();
          const remainingDashboards = dashboards.filter(d => d.id !== dashboard.id);

          if (remainingDashboards.length > 0 && remainingDashboards[0]) {
            this.handleClick(remainingDashboards[0].id);
          } else {
            this.currentDashboard.set(null);
          }
        }

        this.dashboardService.removeDashboard(dashboard.id, allProjects)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.pop.toast(this.translate.instant('dashboard.manager.delete_dashboard'), 'success', 4000, 'delete');

              const remainingDashboards = this.dashboardList();
              const needsReorder = remainingDashboards.some((d, idx) => {
                const ps = d.projectSettings?.find((s: any) => s.project == this.project.id);
                return ps && ps.index !== idx;
              });

              if (needsReorder) {
                const updatedDashboards = remainingDashboards.map((d, idx) => {
                  const ps = d.projectSettings?.find((s: any) => s.project == this.project.id);
                  if (ps) ps.index = idx;
                  return d;
                });

                const payload = {
                  projectId: this.project.id,
                  dashboards: updatedDashboards.map((d) => ({
                    id: d.id,
                    projectSettings: d.projectSettings
                  }))
                };

                this.dashboardService.orderUpdateDashboards(payload)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: () => console.log('✅ Dashboard indexes reordered'),
                    error: (err) => console.error('❌ Error reordering:', err)
                  });
              }
            },
            error: (err) => {
              console.error('❌ Error deleting dashboard:', err);
              this.pop.error(this.translate.instant('status.error'));
            }
          });
      }
    });
  }

  save() {
    const current = this.currentDashboard();

    if (!current) {
      console.warn('⚠️ No dashboard selected to save');
      return;
    }

    const dashboard: PutDashboard = {
      id: current.id,
      title: (current.title || '').trim(),
      description: (current.description || '').trim(),
      autoRefresh: current.autoRefresh,
      projectSettings: current.projectSettings,
    };

    this.dashboardService.updateDashboard(dashboard, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.currentDashboard.set(JSON.parse(JSON.stringify(res)));
          this.pop.success(this.translate.instant('dashboard.manager.save_dashboard'));
        },
        error: (err) => {
          console.error('❌ Error saving dashboard:', err);
          this.pop.error(this.translate.instant('status.error'));
        }
      });
  }

  onDrop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const dashboards = [...this.dashboardList()];
    moveItemInArray(dashboards, event.previousIndex, event.currentIndex);

    dashboards.forEach((dashboard, index) => {
      const ps = dashboard.projectSettings?.find((s: any) => s.project == this.project.id);
      if (ps) ps.index = index;
    });

    const payload = {
      projectId: this.project.id,
      dashboards: dashboards.map((dashboard) => ({
        id: dashboard.id,
        projectSettings: dashboard.projectSettings,
      }))
    };

    this.dashboardService.orderUpdateDashboards(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.pop.success(this.translate.instant('dashboard.manager.save_dashboard'));
        },
        error: (err) => {
          this.pop.error(this.translate.instant('status.error'));
        }
      });
  }

  cancel() {
    this.dialogRef.close({});
  }
}
